import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Plus, Trash2, Save, X, Loader2, AlertTriangle,
    Thermometer, Gauge, Droplets, Search, RefreshCw, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const SENSOR_TYPES = [
    { id: 1, name: 'Датчики температуры', icon: Thermometer, color: 'text-orange-400', bg: 'bg-orange-500/15' },
    { id: 2, name: 'Датчики давления', icon: Gauge, color: 'text-blue-400', bg: 'bg-blue-500/15' },
    { id: 3, name: 'Датчики объёмного расхода', icon: Droplets, color: 'text-cyan-400', bg: 'bg-cyan-500/15' },
];

const DictionariesTab = ({ userInfo }) => {
    const [activeType, setActiveType] = useState(1);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    // Form state
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [registryInput, setRegistryInput] = useState('');
    const [fetchingFgis, setFetchingFgis] = useState(false);
    const [fgisData, setFgisData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    // Delete state
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${API_URL}/api/sensor-dictionaries?type=${activeType}`, authHeaders());
            setItems(res.data);
        } catch (err) {
            setError('Не удалось загрузить справочник');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        resetForm();
    }, [activeType]);

    const resetForm = () => {
        setSearch('');
        setIsFormOpen(false);
        setRegistryInput('');
        setFgisData(null);
        setFormError('');
    };

    const filteredItems = items.filter(item =>
        item.name?.toLowerCase().includes(search.toLowerCase()) ||
        item.registryNumber?.toLowerCase().includes(search.toLowerCase()) ||
        item.designation?.toLowerCase().includes(search.toLowerCase())
    );

    const fetchFromFgis = async () => {
        const num = registryInput.trim();
        if (!num) {
            setFormError('Введите номер Госреестра СИ');
            return;
        }
        setFetchingFgis(true);
        setFormError('');
        setFgisData(null);
        try {
            const res = await axios.get(`${API_URL}/api/fgis-arshin/mit-info?number=${encodeURIComponent(num)}`, authHeaders());
            setFgisData(res.data);
        } catch (err) {
            setFormError(err.response?.data?.message || 'Не удалось получить данные из ФГИС Аршин');
        } finally {
            setFetchingFgis(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!fgisData) {
            setFormError('Сначала загрузите данные из ФГИС Аршин');
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            const payload = {
                registryNumber: fgisData.mitNumber || registryInput.trim(),
                name: fgisData.name || '',
                designation: fgisData.designation || '',
                manufacturer: fgisData.manufacturer || '',
                type: activeType
            };
            await axios.post(`${API_URL}/api/sensor-dictionaries`, payload, authHeaders());
            resetForm();
            await loadData();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Ошибка сохранения в справочник');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/api/sensor-dictionaries/${deleteTarget.id}`, authHeaders());
            setDeleteTarget(null);
            await loadData();
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка удаления');
            setDeleteTarget(null);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-4 animate-slide-down-fade">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-white">Справочники средств измерений</h2>
                    <p className="text-slate-400 text-xs mt-0.5">
                        Данные загружаются автоматически из Госреестра ФГИС Аршин
                    </p>
                </div>
                <button
                    onClick={() => { resetForm(); setIsFormOpen(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition text-sm font-medium whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    Добавить тип СИ
                </button>
            </div>

            {/* Type Selector Tabs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {SENSOR_TYPES.map(type => {
                    const Icon = type.icon;
                    const isActive = activeType === type.id;
                    return (
                        <button
                            key={type.id}
                            onClick={() => setActiveType(type.id)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition text-left ${isActive
                                    ? 'border-blue-500 bg-blue-500/10 text-white'
                                    : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200'
                                }`}
                        >
                            <div className={`p-2 rounded-lg ${isActive ? type.bg : 'bg-slate-700/50'}`}>
                                <Icon className={`w-5 h-5 ${isActive ? type.color : 'text-slate-500'}`} />
                            </div>
                            <span className="text-sm font-medium">{type.name}</span>
                        </button>
                    );
                })}
            </div>

            {/* Error Banner */}
            {error && (
                <div className="px-4 py-3 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</span>
                    <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
                </div>
            )}

            {/* Add Form */}
            {isFormOpen && (
                <div className="bg-slate-800 border border-blue-500 rounded-xl shadow-lg shadow-blue-500/10 p-4 space-y-4 animate-slide-down-fade">
                    <div className="flex items-center justify-between">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                            <Plus className="w-4 h-4 text-blue-400" />
                            Добавление типа СИ из Госреестра
                        </h3>
                        <button onClick={resetForm} disabled={saving || fetchingFgis} className="text-slate-400 hover:text-white">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Step 1: Input registry number */}
                    {!fgisData && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                    Номер Госреестра СИ *
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={registryInput}
                                        onChange={e => setRegistryInput(e.target.value)}
                                        placeholder="Например: 61255-15"
                                        disabled={fetchingFgis}
                                        onKeyDown={e => e.key === 'Enter' && fetchFromFgis()}
                                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-60"
                                    />
                                    <button
                                        type="button"
                                        onClick={fetchFromFgis}
                                        disabled={fetchingFgis || !registryInput.trim()}
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white rounded-lg text-sm font-medium flex items-center gap-2 whitespace-nowrap"
                                    >
                                        {fetchingFgis ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                        Загрузить из ФГИС
                                    </button>
                                </div>
                            </div>
                            {formError && (
                                <div className="text-red-300 text-sm flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> {formError}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Preview & Confirm (ТОЛЬКО доступные поля) */}
                    {fgisData && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-3 flex items-start gap-2">
                                <FileText className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <p className="text-green-300 font-medium">Данные успешно загружены из ФГИС Аршин</p>
                                    <p className="text-green-400/70 text-xs mt-0.5">Проверьте информацию и подтвердите добавление</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1">Номер Госреестра</label>
                                    <div className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm">{fgisData.mitNumber}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1">Наименование</label>
                                    <div className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm">{fgisData.name || '—'}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1">Обозначение</label>
                                    <div className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm">{fgisData.designation || '—'}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1">Изготовитель</label>
                                    <div className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm">{fgisData.manufacturer || '—'}</div>
                                </div>
                            </div>

                            {formError && (
                                <div className="text-red-300 text-sm flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> {formError}
                                </div>
                            )}

                            <div className="flex gap-3 pt-1">
                                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Добавить в справочник
                                </button>
                                <button type="button" onClick={() => { setFgisData(null); setFormError(''); }} disabled={saving} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm">
                                    Изменить номер
                                </button>
                                <button type="button" onClick={resetForm} disabled={saving} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm">
                                    Отмена
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
                    <Loader2 className="w-6 h-6 animate-spin" /> Загрузка…
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="relative">
                        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Поиск по наименованию, обозначению или номеру…"
                            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                    </div>

                    {filteredItems.length === 0 ? (
                        <div className="bg-slate-800 border border-slate-700 rounded-xl p-10 text-center text-slate-500 text-sm">
                            {search ? 'Ничего не найдено' : 'Справочник пуст. Добавьте тип СИ из Госреестра ФГИС Аршин.'}
                        </div>
                    ) : (
                        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-700/50 text-slate-300 uppercase text-xs">
                                    <tr>
                                        <th className="px-4 py-3 font-medium">Наименование</th>
                                        <th className="px-4 py-3 font-medium">№ Госреестра</th>
                                        <th className="px-4 py-3 font-medium hidden lg:table-cell">Обозначение</th>
                                        <th className="px-4 py-3 font-medium hidden xl:table-cell">Изготовитель</th>
                                        <th className="px-4 py-3 font-medium w-20 text-right">Действия</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {filteredItems.map(item => (
                                        <tr key={item.id} className="hover:bg-slate-700/30 transition">
                                            <td className="px-4 py-3 text-white font-medium">{item.name}</td>
                                            <td className="px-4 py-3 text-slate-400">{item.registryNumber || '—'}</td>
                                            <td className="px-4 py-3 text-slate-400 hidden lg:table-cell">{item.designation || '—'}</td>
                                            <td className="px-4 py-3 text-slate-400 hidden xl:table-cell truncate max-w-[200px]">{item.manufacturer || '—'}</td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={() => setDeleteTarget(item)}
                                                    className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-600/20 rounded-lg transition"
                                                    title="Удалить"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Delete Modal */}
            {deleteTarget && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !deleting && setDeleteTarget(null)} />
                    <div className="relative bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-[scaleIn_0.25s_cubic-bezier(0.16,1,0.3,1)]">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-600/20 flex items-center justify-center">
                            <Trash2 className="w-6 h-6 text-red-400" />
                        </div>
                        <h3 className="text-white font-semibold text-center">Удалить запись?</h3>
                        <p className="text-slate-400 text-sm text-center mt-2">
                            Тип СИ <span className="text-white font-medium">«{deleteTarget.name}»</span> ({deleteTarget.registryNumber}) будет удалён из справочника.
                        </p>
                        <div className="flex gap-3 mt-5">
                            <button onClick={confirmDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-medium rounded-lg text-sm flex items-center justify-center gap-2">
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Удалить</>}
                            </button>
                            <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg text-sm">
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DictionariesTab;